import { useEffect, useState } from "react";
import { ITableState } from "taltech-styleguide";
import { QueryState, TableContext, TableState } from "./context";
import { isArray } from "ramda-adjunct";
import { useGetAll } from "../../components-system/api";
import { clone } from "ramda";

const initialState = {
  page: 0,
  sizePerPage: 10,
  total: 0,
};

/**
 * Component: TableProvider
 *
 * This component provides table data and state management to its children components.
 * It uses context to pass down the table state and handles fetching and updating data.
 *
 * @param {object} props - The properties for the TableProvider component.
 * @param {React.ReactNode} props.children - The child components to render within the provider.
 * @param {object} props.props - The additional properties for configuring the table.
 * @returns {JSX.Element} - The rendered TableProvider component.
 */
export const TableProvider: React.FC<any> = ({ children, props }) => {
  const [query, setQuery] = useState<QueryState>(initialState);
  const { data, isLoading, refetch } = useGetAll(query, props?.source as any);
  const [items, setItems] = useState<any>([]);
  const updateState = (updates: Partial<QueryState>) => {
    setQuery((prevState) => ({ ...prevState, ...updates }));
  };

  useEffect(() => {
    const handleRefetch = () => refetch();

    document.addEventListener("refetchTable", handleRefetch);

    return () => {
      document.removeEventListener("refetchTable", handleRefetch);
    };
  }, []);

  useEffect(() => {
    if (isArray(data?.items)) {
      setQuery({ ...query, total: data!.total });
      setItems(data!.items);
    }
  }, [data]);

  const handleTableChange = (type: string, state: ITableState) => {
    let newQuery: Partial<QueryState> = {};
    switch (type) {
      case "sort":
      case "pagination":
        newQuery = {
          ...newQuery,
          page: state.page,
          sizePerPage: state.sizePerPage,
        };
        if (type === "sort") {
          newQuery.sortField = state.sortField;
          newQuery.sortOrder = state.sortOrder;
        }
        break;
      case "filter":
        let newFilters = clone(state.filters);
        if (state.filters) {
          Object.keys(state.filters).map((key) => {
            if (key.includes(".")) {
              const newKey: string = key.split(".").pop() as string;
              newFilters = {
                ...newQuery.filters,
                [newKey]: state.filters[key],
              };
            }
          });
        }
        
        newQuery = {
          ...newQuery,
          page: 0,
          sizePerPage: state.sizePerPage,
          filters: newFilters,
        };
        break;
    }
    updateState(newQuery);
  };

  const result: TableState = {
    items: items! || [],
    total: data?.total ?? 0,
    isLoading,
    query,
    handleTableChange,
    refetch,
  };

  return (
    <TableContext.Provider value={result}>{children}</TableContext.Provider>
  );
};
